import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { Datebox, Dropdown, Textbox } from "capitalrx-components-2.0/lib/forms";
import { Icon, LoadingGroup, LoadingSkeleton } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Paragraph, Subtitle } from "capitalrx-components-2.0/lib/typography";
import { debounce } from "capitalrx-components-2.0/lib/utilities";

import { MedicalClaimListObject, type Status, useListMedicalClaimsQuery } from "@/App/api/claims";
import { MemberClaim, useListMemberClaimsQuery, useListMemberDependentsQuery } from "@/App/api/member";
import {
  ContentPanel,
  DependentDropdown,
  NoResults,
  PageWrapper,
  Pager,
  ThemedButton,
  ThemedCheckbox,
  ThemedDropdown,
} from "@/App/components";
import { StackOrShelf } from "@/App/components/StackOrShelf";
import { TabHeaderButtons } from "@/App/components/TabHeaderButtons";
import { CLAIMS_TYPES, ClaimsTypes, SearchParamKeys } from "@/App/constants/constants";
import { ExperienceContext } from "@/App/context";
import { MemberEligibilityContext } from "@/App/context/MemberEligibilityContext";
import {
  useIsDentalUser,
  useIsMedicareUser,
  useIsPharmacyUser,
  useIsUnifiedUser,
  useIsVisionUser,
  useNavigateClient,
  useNavigateClientNewTab,
} from "@/App/hooks";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";

import { GenerateClaimsForm, GeneratePDFModal } from "./GeneratePDFModal";
import { MedicalClaimListItem } from "./MedicalClaimListItem";
import { PharmacyClaimListItem } from "./PharmacyClaimListItem";

import "./ClaimsPage.scss";
import { isDentalEnabled, isVisionEnabled } from "@/App/utils/featureFlags";

const RESULTS_PER_PAGE = 10;

const changeSearch = debounce((setter: (v: string) => void, value: string, previousValue?: string) => {
  if (value) {
    logEvent(ANALYTIC_EVENTS.CLAIM_HISTORY.searchHistory);
    setter(value);
  } else {
    if (previousValue) {
      logEvent(ANALYTIC_EVENTS.CLAIM_HISTORY.searchClear);
    }
    setter(undefined);
  }
}, 600);

interface DateWithInput extends Date {
  _inputMonth?: number;
  _inputYear?: number;
}

interface ClaimListFilters {
  page_number: number;
  results_per_page: number;
  full_text_search?: string;
  person_code: string;
  month?: number;
  year?: number;
  start_date?: string;
  end_date?: string;
  status?: Status[] | null;
  order_by?: Array<{ [key: string]: string }>;
  coverage_strategy_type?: string;
  type?: ClaimsTypes | null;
}

type DateboxValue = {
  type: string;
  value?: string;
  reason?: string;
};

export const ClaimsPage: FC = () => {
  const { t: translate } = useTranslation();
  const navigate = useNavigateClient();
  const navigateNewTab = useNavigateClientNewTab();
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const { historicalClaimsMessage } = useContext(ExperienceContext);
  const { isUnifiedUser } = useIsUnifiedUser();
  const { isDentalUser } = useIsDentalUser();
  const { isVisionUser } = useIsVisionUser();
  const { isPharmacyUser } = useIsPharmacyUser();
  const hasMedicalBenefits = isUnifiedUser;
  const availableClaimsTypes = useMemo(() => CLAIMS_TYPES.filter((type) => {
    if (type === "pharmacy") {
      return isPharmacyUser;
    }
    if (type === "medical") {
      return hasMedicalBenefits;
    }
    if (type === "dental") {
      return isDentalUser && isDentalEnabled();
    }
    if (type === "vision") {
      return isVisionUser && isVisionEnabled();
    }
    return true;
  }), [isPharmacyUser, hasMedicalBenefits, isDentalUser, isVisionUser, isDentalEnabled, isVisionEnabled]);
  const hasMultipleClaimsTypeOptions = availableClaimsTypes.length > 1;

  useEffect(() => {
    logEvent(ANALYTIC_EVENTS.CLAIM_HISTORY.pageView);
  }, []);

  const [medicarePrescriptionPaymentPlanIndicator, setMedicarePrescriptionPaymentPlanIndicator] = useState(undefined);
  const { isPartD } = useIsMedicareUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const claimsTypeParam = searchParams.get("claims_type") as ClaimsTypes;
  const personCodeParam = searchParams.get("person_code");

  const [pageNumber, setPageNumber] = useState(1);
  const [searchValue, setSearchValue] = useState<string>(undefined);
  const [medicalClaimStatus, setMedicalClaimStatus] = useState<Status | null>(null);
  const [fullTextSearch, setFullTextSearch] = useState(undefined);
  const [personCode, setPersonCode] = useState<string>(() => {
    return personCodeParam || memberEligibility.member.person_code;
  });
  const [showingClaimsModal, setShowingClaimsModal] = useState(false);
  const [serviceDateFrom, setServiceDateFrom] = useState<DateWithInput | null>(null);
  const defaultClaimsType = availableClaimsTypes[0] || "pharmacy";
  const [claimsType, setClaimsType] = useState<ClaimsTypes>(claimsTypeParam || defaultClaimsType);
  const isPharmacyClaimsType = claimsType === "pharmacy";

  const statusOptions = [
    { key: "pended", value: translate("CLAIMS.statusOptions.inProgress") },
    { key: "partially_paid", value: translate("CLAIMS.statusOptions.partiallyApproved") },
    { key: "paid", value: translate("CLAIMS.statusOptions.approved") },
    { key: "denied", value: translate("CLAIMS.statusOptions.denied") },
  ];

  const claimListFilters = useMemo(() => {
    const rawFilters: ClaimListFilters = {
      page_number: pageNumber,
      results_per_page: RESULTS_PER_PAGE,
      full_text_search: fullTextSearch,
      person_code: personCode,
    };

    if (serviceDateFrom && serviceDateFrom instanceof Date && !isNaN(serviceDateFrom.getTime())) {
      // Datebox stores user's actual input to avoid timezone issues
      const month = serviceDateFrom._inputMonth || serviceDateFrom.getMonth() + 1;
      const year = serviceDateFrom._inputYear || serviceDateFrom.getFullYear();

      if (isPharmacyClaimsType) {
        const startDate = new Date(year, month - 1, 1);
        const endDate = new Date(year, month, 0);

        rawFilters.start_date = startDate.toISOString().split("T")[0];
        rawFilters.end_date = endDate.toISOString().split("T")[0];
      } else {
        rawFilters.month = month;
        rawFilters.year = year;
      }
    }

    if (!isPharmacyClaimsType) {
      if (medicalClaimStatus !== null) {
        rawFilters.status = [medicalClaimStatus];
      }
      if (claimsType !== "medical") {
        /* Only include the type filter if it's not "medical" because medical is the default
           and also includes professional and institutional claims */
        rawFilters.type = claimsType;
      }
      rawFilters.order_by = [{ minimum_service_date: "desc" }];
    } else {
      rawFilters.order_by = [{ date_of_service: "desc" }];
    }

    if (medicarePrescriptionPaymentPlanIndicator === "Y") {
      rawFilters.coverage_strategy_type = "medicare_prescription_payment_plan";
    }
    return rawFilters;
  }, [
    pageNumber,
    fullTextSearch,
    personCode,
    medicarePrescriptionPaymentPlanIndicator,
    medicalClaimStatus,
    serviceDateFrom,
    isPharmacyClaimsType,
    claimsType,
  ]);

  const {
    data: medicalClaims,
    isLoading: isLoadingMedicalClaims,
    isRefetching: isRefetchingMedicalClaims,
    refetch: refetchMedicalClaims,
  } = useListMedicalClaimsQuery(claimListFilters, !isPharmacyClaimsType);

  const {
    data: pharmacyClaims,
    isLoading: isLoadingPharmacyClaims,
    isRefetching: isRefetchingPharmacyClaims,
    refetch: refetchPharmacyClaims,
  } = useListMemberClaimsQuery(claimListFilters, isPharmacyClaimsType);

  const { data: dependents } = useListMemberDependentsQuery();

  const minorDependents = useMemo(() => {
    return (dependents?.results || []).filter((dependent) => dependent.member?.is_minor_dependent);
  }, [dependents]);

  useEffect(() => {
    changeSearch(setFullTextSearch, searchValue, fullTextSearch);
  }, [searchValue]);

  /**
   * Handle the search changing. Since this is a debounced search,
   * it doesn't happen on the regular render cycle.
   */
  useEffect(() => {
    if (isPharmacyClaimsType) {
      refetchPharmacyClaims();
    } else {
      refetchMedicalClaims();
    }
  }, [fullTextSearch, personCode, medicalClaimStatus, serviceDateFrom]);

  const { isCompressed } = useResponsive({
    isCompressed: {
      mobile: true,
      tablet: false,
      desktop: false,
    },
  });

  const onToggleShowOnlyMedicarePrescriptionPaymentPlanClaims = useCallback((value: boolean) => {
    logEvent(ANALYTIC_EVENTS.CLAIM_HISTORY.mpppCheckboxToggle, { enabled: value });
    if (value) {
      setMedicarePrescriptionPaymentPlanIndicator(() => {
        setPageNumber(1);
        return "Y";
      });
    } else {
      setMedicarePrescriptionPaymentPlanIndicator((): string | undefined => {
        setPageNumber(1);
        return undefined;
      });
    }
  }, []);

  const onGeneratePdf = useCallback(
    async (formData: GenerateClaimsForm) => {
      const searchParams = new URLSearchParams();
      if (formData.spanType === "ytd") {
        searchParams.append("ytd", "true");
      } else {
        if (formData.month) {
          searchParams.append("month", formData.month);
        }
        if (formData.year) {
          searchParams.append("year", formData.year);
        }
      }

      if (personCode) {
        searchParams.append("person_code", personCode);
      }

      if (medicarePrescriptionPaymentPlanIndicator) {
        searchParams.append("medicare_prescription_payment_plan_indicator", medicarePrescriptionPaymentPlanIndicator);
      }

      if (isViewingDataholder) {
        searchParams.append(SearchParamKeys.MEMBER_ELIGIBILITY_MEMBER_ID, memberEligibility.member.id.toString());
      }

      if (isPharmacyClaimsType) {
        logEvent(ANALYTIC_EVENTS.CLAIM_HISTORY.generatePharmacyClaimsHistory);
        navigateNewTab("/print-pharmacy-claims-history", searchParams);
      } else {
        if (medicalClaimStatus) {
          searchParams.append("status", medicalClaimStatus);
        }
        logEvent(ANALYTIC_EVENTS.CLAIM_HISTORY.generateMedicalClaimsHistory);
        navigateNewTab("/print-medical-claims-history", searchParams);
      }

      let event = ANALYTIC_EVENTS.CLAIM_HISTORY.generatePharmacyClaimsHistory;
      let printUrl = "/print-pharmacy-claims-history";
      if (!isPharmacyClaimsType) {
        event = ANALYTIC_EVENTS.CLAIM_HISTORY.generateMedicalClaimsHistory;
        printUrl = "/print-medical-claims-history";
      }
      logEvent(event);
      navigateNewTab(printUrl, searchParams);
    },
    [personCode, medicarePrescriptionPaymentPlanIndicator, isPharmacyClaimsType, medicalClaimStatus],
  );

  const onClaimView = useCallback(
    (claim: MedicalClaimListObject | MemberClaim) => {
      logEvent(ANALYTIC_EVENTS.CLAIM_HISTORY.viewClaim);
      const claimType = claimsType || "pharmacy";
      navigate(`/claims-history/${claimType}/${claim.id}/${claim.person_code}`);
    },
    [claimsType],
  );

  const handleClaimsTypeChange = (claimTypeIndex: number) => {
    const currentClaimsType = availableClaimsTypes[claimTypeIndex];
    switch (currentClaimsType) {
      case "medical":
        logEvent(ANALYTIC_EVENTS.CLAIM_HISTORY.toggleMedical);
        break;
      case "pharmacy":
        logEvent(ANALYTIC_EVENTS.CLAIM_HISTORY.togglePharmacy);
        break;
      case "dental":
        logEvent(ANALYTIC_EVENTS.CLAIM_HISTORY.toggleDental);
        break;
      case "vision":
        logEvent(ANALYTIC_EVENTS.CLAIM_HISTORY.toggleVision);
        break;
      default:
        break;
    }
    setSearchValue(null);
    setPageNumber(1);
    setSearchParams((prev) => {
      prev.set("claims_type", currentClaimsType);
      if (personCode !== memberEligibility.member.person_code) {
        prev.set("person_code", personCode);
      } else {
        prev.delete("person_code");
      }
      return prev;
    });
    setClaimsType(currentClaimsType as ClaimsTypes);
  };

  const handleClaimsStatusFilterChange = (value: string | null) => {
    setPageNumber(1);
    if (value) {
      logEvent(ANALYTIC_EVENTS.CLAIM_HISTORY.statusFilterChange, { status: value });
    } else {
      logEvent(ANALYTIC_EVENTS.CLAIM_HISTORY.statusFilterClear);
    }
    setMedicalClaimStatus(value as Status | null);
  };

  const handleMedicalDependentFilterChange = (value: string) => {
    setPageNumber(1);
    setPersonCode(value);
    searchParams.append("person_code", personCode);
    setSearchParams((prev) => {
      if (value !== memberEligibility.member.person_code) {
        prev.set("person_code", value);
      } else {
        prev.delete("person_code");
      }
      return prev;
    });
    logEvent(ANALYTIC_EVENTS.CLAIM_HISTORY.dependentFilterChange, { personCode: value });
  };

  const debouncedSetServiceDate = useCallback(
    debounce((parsedDate: DateWithInput | null, monthValue: number | null, yearValue: number | null) => {
      if (parsedDate) {
        setServiceDateFrom(parsedDate);
        setPageNumber(1);
        logEvent(ANALYTIC_EVENTS.CLAIM_HISTORY.serviceDateFilterChange, {
          dateType: "from",
          date: parsedDate,
          month: monthValue,
          year: yearValue,
        });
      } else {
        setServiceDateFrom(null);
        setPageNumber(1);
        logEvent(ANALYTIC_EVENTS.CLAIM_HISTORY.serviceDateClear);
      }
    }, 800),
    [],
  );

  const handleServiceDateFromChange = (date: Date | string | DateboxValue | null) => {
    if (!date || date === null || date === "") {
      debouncedSetServiceDate(null, null, null);
      return;
    }

    let parsedDate: DateWithInput | null = null;
    let monthValue: number | null = null;
    let yearValue: number | null = null;

    // Datebox returns objects with type/value during typing
    if (date && typeof date === "object" && "type" in date) {
      if (date.type === "invalid") {
        return;
      }
      if (date.value && typeof date.value === "string") {
        const parts = date.value.split("/");
        if (parts.length === 3 && !parts.some((p: string) => p.includes("_"))) {
          monthValue = parseInt(parts[0], 10);
          const day = parseInt(parts[1], 10);
          yearValue = parseInt(parts[2], 10);
          parsedDate = new Date(yearValue, monthValue - 1, day, 12, 0, 0);
        } else {
          // Incomplete date, don't update state yet
          return;
        }
      }
    } else if (date instanceof Date) {
      // Datebox Date objects are timezone-adjusted, extract from DOM to get user's actual input
      const inputElement = document.querySelector('[data-testid="service-date"]') as HTMLInputElement;
      const inputValue = inputElement?.value;

      if (inputValue && inputValue.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
        const parts = inputValue.split("/");
        monthValue = parseInt(parts[0], 10);
        const day = parseInt(parts[1], 10);
        yearValue = parseInt(parts[2], 10);

        parsedDate = new Date(yearValue, monthValue - 1, day, 12, 0, 0);
      } else {
        // Fallback: adjust for timezone offset
        const localDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
        monthValue = localDate.getMonth() + 1;
        yearValue = localDate.getFullYear();
        parsedDate = localDate;
      }
    } else if (typeof date === "string" && date) {
      const parts = date.split("/");
      if (parts.length === 3) {
        monthValue = parseInt(parts[0], 10);
        const day = parseInt(parts[1], 10);
        yearValue = parseInt(parts[2], 10);
        parsedDate = new Date(yearValue, monthValue - 1, day, 12, 0, 0);
      }
    }

    if (parsedDate && !isNaN(parsedDate.getTime()) && monthValue && yearValue) {
      // Store user's actual input to avoid timezone issues in API calls
      parsedDate._inputMonth = monthValue;
      parsedDate._inputYear = yearValue;

      debouncedSetServiceDate(parsedDate, monthValue, yearValue);
    }
  };

  const activeClaims = isPharmacyClaimsType ? pharmacyClaims : medicalClaims;
  const isLoadingListing = isLoadingPharmacyClaims || isRefetchingPharmacyClaims || isLoadingMedicalClaims || isRefetchingMedicalClaims;

  const claimsTypeSelectElement = isCompressed ? (
    <div style={{ maxWidth: "300px" }}>
      <ThemedDropdown
        className="claims-type-select-dropdown"
        onChange={handleClaimsTypeChange}
        value={availableClaimsTypes.indexOf(claimsType)}
        label={translate("CLAIMS.claimsPage.claimsTypeDropdownLabel")}
        aria-label={translate("CLAIMS.claimsPage.claimsTypeDropdownLabel")}
      >
        {availableClaimsTypes.map((type) => (
          <ThemedDropdown.Option value={availableClaimsTypes.indexOf(type)} key={type}>
            {translate(`CLAIMS.claimsPage.${type}`)}
          </ThemedDropdown.Option>
        ))}
      </ThemedDropdown>
    </div>
  ) : (
    <TabHeaderButtons
      onChange={handleClaimsTypeChange}
      tabTitles={availableClaimsTypes.map((t) => translate(`CLAIMS.claimsPage.${t}`))}
      value={availableClaimsTypes.indexOf(claimsType)}
    />
  );

  return (
    <PageWrapper>
      <GeneratePDFModal
        visible={showingClaimsModal}
        onClose={() => setShowingClaimsModal(false)}
        onSubmit={onGeneratePdf}
        claimsType={claimsType}
      />
      <ContentPanel
        title={
          <Stack>
            <StackOrShelf>
              <Stack>
                <Subtitle>{translate("CLAIMS.claimsPage.claimsHistory")}</Subtitle>
                {historicalClaimsMessage && <Paragraph testId="historical-claims-message">{historicalClaimsMessage}</Paragraph>}
              </Stack>
              <Shelf.Spacer />
              <ThemedButton
                testId="generate-claims-pdf-button"
                disabled={(activeClaims?.results || []).length === 0}
                appearance="secondary"
                onClick={() => setShowingClaimsModal(true)}
              >
                {translate("CLAIMS.claimsPage.generatePdf")}
              </ThemedButton>
            </StackOrShelf>
          </Stack>
        }
        showBorder={true}
      >
        <Stack spacing="large" className="claims-section" style={{ paddingTop: 0 }}>
          <Stack spacing="large">
            <DependentDropdown
              testId="claim-dependents-view-dropdown"
              onChange={handleMedicalDependentFilterChange}
              value={personCode}
              label={translate("CLAIMS.claimsPage.viewClaimsFor")}
              aria-label={translate("CLAIMS.claimsPage.claimDependentsViewDropdownLabel")}
              dependents={minorDependents}
              member={memberEligibility.member}
            />
            {hasMultipleClaimsTypeOptions && <>{claimsTypeSelectElement}</>}
          </Stack>
          <Grid>
            {isPharmacyClaimsType && (
              <Grid.Span columns={isCompressed ? 12 : 9}>
                <Textbox
                  prefix={<Icon name="search" />}
                  testId="pharmacy-search-claims-textbox"
                  value={searchValue}
                  onChange={setSearchValue}
                  label={translate("CLAIMS.claimsPage.searchClaimsTextboxLabel")}
                  placeholder={translate("CLAIMS.claimsPage.pharmacySearchClaimsTextboxPlaceHolder")}
                />
              </Grid.Span>
            )}
            {!isPharmacyClaimsType && (
              <Grid.Span columns={isCompressed ? 12 : 6}>
                <Textbox
                  prefix={<Icon name="search" />}
                  testId="search-claims-textbox"
                  value={searchValue}
                  onChange={setSearchValue}
                  label={translate("CLAIMS.claimsPage.searchClaimsTextboxLabel")}
                  placeholder={translate("CLAIMS.claimsPage.medicalSearchClaimsTextboxPlaceHolder")}
                />
              </Grid.Span>
            )}
            {!isPharmacyClaimsType && (
              <Grid.Span columns={isCompressed ? 12 : 3}>
                <Dropdown
                  testId="status-dropdown"
                  onChange={handleClaimsStatusFilterChange}
                  value={medicalClaimStatus}
                  label={translate("CLAIMS.statusOptions.dropdownLabelText")}
                  placeholder={translate("CLAIMS.claimsPage.dropdownSelectPlaceholder")}
                  clearable
                >
                  {statusOptions.map((status) => {
                    return (
                      <Dropdown.Option value={status.key} key={status.key}>
                        {status.value}
                      </Dropdown.Option>
                    );
                  })}
                </Dropdown>
              </Grid.Span>
            )}
            <Grid.Span columns={isCompressed ? 12 : 3}>
              <Stack spacing="none">
                <Datebox
                  testId="service-date"
                  label={translate("CLAIMS.claimsPage.serviceDateLabel")}
                  value={serviceDateFrom}
                  onChange={handleServiceDateFromChange}
                  aria-label={translate("CLAIMS.claimsPage.serviceDate")}
                />
                <small style={{ color: "#3E5D6F" }}>{translate("CLAIMS.claimsPage.selectDate")}</small>
              </Stack>
            </Grid.Span>
          </Grid>
          {isPartD && isPharmacyClaimsType && (
            <ThemedCheckbox
              value={medicarePrescriptionPaymentPlanIndicator === "Y"}
              onChange={onToggleShowOnlyMedicarePrescriptionPaymentPlanClaims}
              label={translate("CLAIMS.claimsPage.themedCheckboxLabel")}
              className="mppp-checkbox"
            />
          )}
          <LoadingGroup loading={isLoadingListing}>
            <LoadingGroup.Placeholder
              fallback={
                <Grid spacing="none">
                  <Grid.Span columns={isCompressed ? 12 : 6}>
                    <LoadingSkeleton count={6} wrapper={Stack} style={{ height: "200px", borderRadius: "0.5rem" }} />
                  </Grid.Span>
                  <Grid.Span columns={isCompressed ? 12 : 6}>
                    <LoadingSkeleton count={6} wrapper={Stack} style={{ height: "200px", borderRadius: "0.5rem" }} />
                  </Grid.Span>
                </Grid>
              }
            >
              {!isPharmacyClaimsType && (
                <Grid>
                  {medicalClaims?.results.map((claim) => {
                    return (
                      <Grid.Span columns={isCompressed ? 12 : 6} key={claim.medical_claim.id}>
                        <MedicalClaimListItem onClaimView={onClaimView} medical_claim={claim.medical_claim} />
                      </Grid.Span>
                    );
                  })}
                </Grid>
              )}
              {isPharmacyClaimsType && (
                <Grid>
                  {pharmacyClaims?.results.map((claim) => {
                    return (
                      <Grid.Span columns={isCompressed ? 12 : 6} key={claim.claim.id}>
                        <PharmacyClaimListItem onClaimView={onClaimView} pharmacy_claim={claim.claim} />
                      </Grid.Span>
                    );
                  })}
                </Grid>
              )}
            </LoadingGroup.Placeholder>
          </LoadingGroup>
          {!isLoadingListing && (activeClaims?.results || []).length === 0 && (
            <NoResults message={translate("CLAIMS.claimsPage.noResultsMessage")} />
          )}
          {(activeClaims?.results || []).length > 0 && (
            <Pager
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              resultCount={String(activeClaims?.result_count)}
              resultsPerPage={RESULTS_PER_PAGE}
              disabled={isLoadingMedicalClaims || isLoadingPharmacyClaims || isRefetchingMedicalClaims || isRefetchingPharmacyClaims}
              analyticsTag={ANALYTIC_EVENTS.CLAIM_HISTORY.pagination.pageNumber}
            />
          )}
        </Stack>
      </ContentPanel>
    </PageWrapper>
  );
};
